import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import axios from "axios";
import axiosInstance from "../Backend/IghotaVirtualLearning/axiosInstance.js";
import { createSlug } from "./utils.js";

// Cache configuration
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
	const cached = cache.get(key);
	if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
		return cached.data;
	}
	return null;
};

// Utility function for retrying API calls
const retryApiCall = async ({
	apiCall,
	retryCount = 2,
	initialDelay = 5000,
	timeout = 15000,
	onRetry = () => {},
	validateResponse = () => true,
}) => {
	let attempt = 0;
	const initialDelayValue = initialDelay;

	while (attempt <= retryCount) {
		console.log("Called");
		const currentDelay = initialDelayValue * Math.pow(2, attempt);

		try {
			const response = await Promise.race([
				apiCall(),
				new Promise((_, reject) =>
					setTimeout(
						() => reject(new Error("Request timeout")),
						timeout
					)
				),
			]);

			if (response.status === 200) {
				if (validateResponse(response)) {
					console.log("Response data: ending now", response.data);
					return response;
				}

				throw new Error("Invalid response data");
			}

			throw new Error(`Received status code: ${response.status}`);
		} catch (err) {
			if (axios.isCancel(err)) {
				throw err;
			}

			const status = err?.response?.status;

			if (status && status >= 400 && status < 500 && status !== 408) {
				const error = new Error(`Client error: ${status}`);
				error.attempts = attempt + 1;
				error.originalError = err;
				throw error;
			}

			attempt++;

			if (attempt > retryCount) {
				const error = new Error(`Failed after ${attempt} attempts`);
				error.attempts = attempt;
				error.originalError = err;
				throw error;
			}

			const shouldRetry =
				status >= 500 ||
				status === 408 ||
				err.code === "ECONNABORTED" ||
				err.message === "Request timeout" ||
				!status;

			if (!shouldRetry) {
				const error = new Error("Non-retriable error");
				error.attempts = attempt;
				error.originalError = err;
				throw error;
			}

			onRetry({
				attempt,
				delay: currentDelay,
				error: err,
			});

			await new Promise((resolve) => setTimeout(resolve, currentDelay));
		}
	}
};

export const useFetchStrapiData = () => {
	const { subjectSlug, courseSlug, lessonId } = useParams();
	const mountedRef = useRef(true);
	const cancelSourceRef = useRef(null);
	const requestInProgressRef = useRef(false);
	const previousPathRef = useRef("");
	const [state, setState] = useState({
		strapiData: null,
		isLoading: true,
		error: null,
	});

	useEffect(() => {
		const currentPath = `${subjectSlug}/${courseSlug}/${lessonId}`;

		// If the path has changed, reset cache and state
		if (
			previousPathRef.current &&
			previousPathRef.current !== currentPath
		) {
			cache.clear(); // Optional: if you want to clear cache entirely
			setState({
				strapiData: null,
				isLoading: true,
				error: null,
			});
		}

		previousPathRef.current = currentPath;

		const fetchData = async () => {
			if (cancelSourceRef.current) {
				cancelSourceRef.current.cancel("New request started");
				cancelSourceRef.current = null;
			}

			try {
				// Updated to include lessonId in cache key
				const cacheKey = `${subjectSlug}-${courseSlug}-${lessonId}`;
				const cachedData = getCachedData(cacheKey);

				if (cachedData) {
					if (mountedRef.current) {
						setState({
							strapiData: cachedData,
							isLoading: false,
							error: null,
						});
					}
					return;
				}

				if (!subjectSlug || !courseSlug) {
					if (mountedRef.current) {
						setState((prev) => ({
							...prev,
							isLoading: false,
							error: "Invalid URL parameters",
						}));
					}
					return;
				}

				cancelSourceRef.current = axios.CancelToken.source();
				requestInProgressRef.current = true;

				const response = await retryApiCall({
					apiCall: async () => {
						if (!mountedRef.current) {
							throw new Error("Component unmounted");
						}

						const LESSON_ENDPOINT_URL = `/courses?filters[slug][$eqi]=${encodeURIComponent(courseSlug)}&populate=units.lessons.quiz_question.question_option&populate=units.lessons.quizzes.questions.question_option&pagination[limit]=-1`;

						return axiosInstance.get(LESSON_ENDPOINT_URL, {
							cancelToken: cancelSourceRef.current.token,
						});
					},
					// validateResponse: (response) =>
					// 	response?.data?.data?.[0] != null,
					validateResponse: (response) => {
						const data = response?.data?.data;
						return !!data;
					},
					onRetry: ({ attempt, delay, error }) => {
						if (mountedRef.current) {
							console.log(
								`Attempt ${attempt} failed. Retrying in ${delay}ms...`,
								error
							);
							Sentry.captureException(error, {
								extra: {
									subjectSlug,
									courseSlug,
									lessonId,
									attempt,
									delay,
								},
							});
						}
					},
				});

				if (!mountedRef.current) return;

				const courseData = response?.data?.data[0];

				if (!courseData) {
					throw new Error("No course data found");
				}

				const processedData = {
					course: courseData?.title || "Unknown Course",
					id: courseData?.id,
					slug: courseData?.slug || "",
					units:
						courseData?.units?.map((unit) => ({
							id: unit.id,
							title: unit.title || "Untitled Unit",
							slug: unit.slug || "",
							lessons:
								unit?.lessons?.map((lesson) => ({
									id: lesson.id,
									title: lesson.title || "Untitled Lesson",
									slug: lesson.slug || "",
									path: `/courses/${subjectSlug}/${courseSlug}/${unit.slug || ""}/${lesson.slug || ""}/${lesson.id}`,
									videoUrl: lesson.videoUrl,
									questions:
										lesson.quiz_question?.map(
											(question) => ({
												id: question.id,
												title: question.title,
												correctAnswer:
													question.correctAnswer,
												options:
													question.question_option?.map(
														(option) => ({
															id: option.id,
															label: option.label,
															answer: option.answer,
															isCorrect:
																option.isCorrect,
															explanation:
																option.explanation ||
																"No explanation provided.",
														})
													) || [],
											})
										) || [],
								})) || [],
						})) || [],
				};

				if (mountedRef.current) {
					setState({
						strapiData: processedData,
						isLoading: false,
						error: null,
					});

					// Save new data in cache with full key including lessonId
					cache.set(cacheKey, {
						data: processedData,
						timestamp: Date.now(),
					});
				}
			} catch (err) {
				if (axios.isCancel(err)) {
					console.log("Request cancelled:", err.message);
					if (
						mountedRef.current &&
						err.message !== "Component unmounted"
					) {
						setState((prev) => ({
							...prev,
							isLoading: false,
							error: "Request cancelled",
						}));
					}
					return;
				}

				if (mountedRef.current) {
					console.error("Fetch error:", err);
					setState({
						strapiData: null,
						isLoading: false,
						error: err.message || "Failed to load data",
					});
					Sentry.captureException(err.originalError || err, {
						extra: {
							subjectSlug,
							courseSlug,
							lessonId,
							attempts: err.attempts || 1,
						},
					});
				}
			} finally {
				requestInProgressRef.current = false;
			}
		};

		fetchData();

		return () => {
			mountedRef.current = false;
			if (cancelSourceRef.current) {
				cancelSourceRef.current.cancel("Component unmounted");
				cancelSourceRef.current = null;
			}
		};
	}, [subjectSlug, courseSlug, lessonId]);

	return {
		quizData: state.strapiData,
		isLoading: state.isLoading,
		error: state.error,
	};
};

export const useFetchCourseData = (courseId) => {
	const mountedRef = useRef(true);
	const cancelSourceRef = useRef(null);
	const requestInProgressRef = useRef(false);
	const [state, setState] = useState({
		courseData: null,
		isLoading: true,
		error: null,
	});

	useEffect(() => {
		const fetchData = async () => {
			if (cancelSourceRef.current) {
				cancelSourceRef.current.cancel("New request started");
				cancelSourceRef.current = null;
			}

			try {
				const cacheKey = `course-${courseId}`;
				const cachedData = getCachedData(cacheKey);

				if (cachedData) {
					if (mountedRef.current) {
						setState({
							courseData: cachedData,
							isLoading: false,
							error: null,
						});
					}
					return;
				}

				if (!courseId) {
					if (mountedRef.current) {
						setState((prev) => ({
							...prev,
							isLoading: false,
							error: "Invalid course ID",
						}));
					}
					return;
				}

				cancelSourceRef.current = axios.CancelToken.source();
				requestInProgressRef.current = true;

				// Try multiple approaches to fetch the course
				let response;

				// Method 1: Try direct ID lookup first
				try {
					response = await retryApiCall({
						apiCall: async () => {
							if (!mountedRef.current) {
								throw new Error("Component unmounted");
							}

							const DIRECT_COURSE_URL = `/courses/${courseId}?populate=units.lessons.quiz_question.question_option&populate=units.lessons.quizzes.questions.question_option&populate=units.materials`;
							return await axiosInstance.get(DIRECT_COURSE_URL, {
								cancelToken: cancelSourceRef.current?.token,
							});
						},
						validateResponse: (response) => {
							console.log("Direct API Response:", response?.data);

							// Strapi v4 single item response structure
							// For direct ID lookup, data should be an object (not array)
							const isValid =
								response?.data?.data != null &&
								typeof response.data.data === "object" &&
								!Array.isArray(response.data.data); // Direct lookup returns object, not array

							console.log("Validation result:", isValid);
							return isValid;
						},
						onRetry: ({ attempt, delay, error }) => {
							console.log(
								"Direct fetch retry attempt:",
								attempt,
								"Error:",
								error.message
							);
							if (mountedRef.current) {
								Sentry.captureException(error, {
									extra: {
										courseId,
										method: "direct",
										attempt,
										delay,
									},
								});
							}
						},
					});

					console.log("Direct lookup successful:", response?.data);
				} catch (directError) {
					console.log(
						"Direct course fetch failed, trying filter approach...",
						directError.message
					);

					// Method 2: Fall back to filter approach if direct lookup fails
					try {
						response = await retryApiCall({
							apiCall: async () => {
								if (!mountedRef.current) {
									throw new Error("Component unmounted");
								}

								// Use filters to find the course by ID
								const FILTER_COURSE_URL = `/courses?filters[documentId][$eq]=${courseId}&populate=units.lessons.quiz_question.question_option&populate=units.lessons.quizzes.questions.question_option&populate=units.materials`;
								return await axiosInstance.get(
									FILTER_COURSE_URL,
									{
										cancelToken:
											cancelSourceRef.current?.token,
									}
								);
							},
							validateResponse: (response) => {
								console.log(
									"Filter API Response:",
									response?.data
								);

								// Filter approach returns array
								const isValid =
									response?.data?.data != null &&
									Array.isArray(response.data.data) &&
									response.data.data.length > 0;

								console.log(
									"Filter validation result:",
									isValid
								);
								return isValid;
							},
							onRetry: ({ attempt, delay, error }) => {
								console.log(
									"Filter fetch retry attempt:",
									attempt,
									"Error:",
									error.message
								);
								if (mountedRef.current) {
									Sentry.captureException(error, {
										extra: {
											courseId,
											method: "filter",
											attempt,
											delay,
										},
									});
								}
							},
						});

						// For filter response, get first item from array
						if (
							response?.data?.data &&
							Array.isArray(response.data.data)
						) {
							// Modify response to match direct lookup structure
							response.data.data = response.data.data[0];
						}

						console.log(
							"Filter lookup successful:",
							response?.data
						);
					} catch (filterError) {
						console.log(
							"Filter approach also failed:",
							filterError.message
						);
						throw filterError; // Re-throw to be caught by outer try-catch
					}
				}

				// Check if component is still mounted before processing
				if (!mountedRef.current) {
					console.log("Component unmounted, aborting...");
					return;
				}

				// Extract course data from the correct path
				const courseData = response?.data?.data;

				if (!courseData) {
					throw new Error("No course data found");
				}

				// Process the course data to match your expected format
				const processedData = {
					id: courseData.id,
					documentId: courseData.documentId,
					title: courseData.title || "Unknown Course",
					description: courseData.description,
					slug: courseData.slug || createSlug(courseData.title || ""),
					createdAt: courseData.createdAt,
					updatedAt: courseData.updatedAt,
					thumbnail: courseData.thumbnail || "#880808", // Red by default
					units:
						courseData.units?.map((unit) => ({
							id: unit.id,
							documentId: unit.documentId,
							title: unit.title || "Untitled Unit",
							slug: unit.slug || createSlug(unit.title || ""),
							createdAt: unit.createdAt,
							updatedAt: unit.updatedAt,
							lessons:
								unit.lessons?.map((lesson) => ({
									id: lesson.id,
									documentId: lesson.documentId,
									title: lesson.title || "Untitled Lesson",
									slug:
										lesson.slug ||
										createSlug(lesson.title || ""),
									createdAt: lesson.createdAt,
									updatedAt: lesson.updatedAt,
									videoUrl: lesson.videoUrl,
									// Legacy: a lesson's original single, unnamed
									// quiz_question list — kept for any lesson that
									// hasn't been migrated to a real Quiz yet.
									questions:
										lesson.quiz_question?.map(
											(question) => ({
												id: question.id,
												title: question.title,
												correctAnswer:
													question.correctAnswer,
												options:
													question.question_option?.map(
														(option) => ({
															id: option.id,
															label: option.label,
															answer: option.answer,
															isCorrect:
																option.isCorrect,
															explanation:
																option.explanation ||
																"No explanation provided.",
														})
													) || [],
											})
										) || [],
									// A lesson can now have several of these,
									// each independently named and published.
									quizzes:
										lesson.quizzes?.map((quiz) => ({
											id: quiz.id,
											documentId: quiz.documentId,
											title: quiz.title,
											questions:
												quiz.questions?.map(
													(question) => ({
														id: question.id,
														title: question.title,
														correctAnswer:
															question.correctAnswer,
														options:
															question.question_option?.map(
																(option) => ({
																	id: option.id,
																	label: option.label,
																	answer: option.answer,
																	isCorrect:
																		option.isCorrect,
																	explanation:
																		option.explanation ||
																		"No explanation provided.",
																})
															) || [],
													})
												) || [],
										})) || [],
								})) || [],
						})) || [],
				};

				// Final mounted check before setting state
				if (mountedRef.current) {
					setState({
						courseData: processedData,
						isLoading: false,
						error: null,
					});

					// Save new data in cache
					cache.set(cacheKey, {
						data: processedData,
						timestamp: Date.now(),
					});
				} else {
					console.log("Component unmounted before state update");
				}
			} catch (err) {
				if (axios.isCancel(err)) {
					console.log("Request cancelled:", err.message);
					if (
						mountedRef.current &&
						err.message !== "Component unmounted"
					) {
						setState((prev) => ({
							...prev,
							isLoading: false,
							error: "Request cancelled",
						}));
					}
					return;
				}

				if (mountedRef.current) {
					console.error("Fetch course error:", err);
					setState({
						courseData: null,
						isLoading: false,
						error: err.message || "Failed to load course data",
					});
					Sentry.captureException(err.originalError || err, {
						extra: {
							courseId,
							attempts: err.attempts || 1,
						},
					});
				}
			} finally {
				requestInProgressRef.current = false;
			}
		};

		// Reset mounted ref when effect runs
		mountedRef.current = true;
		fetchData();

		return () => {
			mountedRef.current = false;
			if (cancelSourceRef.current) {
				cancelSourceRef.current.cancel("Component unmounted");
				cancelSourceRef.current = null;
			}
		};
	}, [courseId]);

	return {
		courseData: state.courseData,
		isLoading: state.isLoading,
		error: state.error,
	};
};
